// src/api/platformMembersApi.ts
//
// CRM BFF membership API for Settings → Members/Invitations.
//
// Replaces the browser-direct-to-Central `centralMembersApi` for the Settings
// Members tab. All requests go to the CRM BFF at `/api/platform/*` via the shared
// `baseApi` (cookie/session auth, boot-gated, refresh-aware). The BROWSER NEVER
// CALLS CENTRAL and never needs a Central access token here — the server holds
// the workspace-scoped Central token and forwards on our behalf.
//
// The workspace is derived server-side from the authenticated session; these
// endpoints intentionally take NO workspaceId argument, so the browser cannot
// select another workspace.
//
// The BFF returns already-sanitized shapes matching @levelup/central-auth's
// `WorkspaceMember` / `PendingInvite` / `InviteRoleOption`, so transforms here
// are just envelope unwrapping.

import { baseApi } from './baseApi';
import type {
  PendingInvite,
  InviteRoleOption,
  CentralRole,
  AssignableAppsResponse,
  InviteAppAssignments,
} from '@levelup/central-auth';
// Central's member rows carry per-app grants (Central PR #502). `appRoles` is
// not yet on central-auth's `WorkspaceMember`, so ui-management publishes the
// structurally-widened row type; it is the canonical shape the shared
// membership UI consumes.
import type { WorkspaceMemberWithAppRoles } from '@levelup/ui-management/membership';

interface MembersEnvelope {
  members?: WorkspaceMemberWithAppRoles[];
}
interface InvitesEnvelope {
  invites?: PendingInvite[];
}
interface OptionsEnvelope {
  options?: InviteRoleOption[];
}

export interface PlatformInviteRequest {
  email: string;
  role: CentralRole;
  templateId?: string;
  message?: string;
  /**
   * Canonical Central app id of the app that INITIATED this invite (CRM = "crm").
   * The invite's origin; Central persists it and routes the user to this app
   * after acceptance. Forwarded verbatim to the BFF → Central; never derived from
   * appRoles/role/host/URL/workspace.
   */
  originatingAppId?: string;
  // Central PR #479 — per-app assignments (appId → AppRole). Central validates.
  appRoles?: InviteAppAssignments;

  /**
   * Where acceptance lands. Forwarded verbatim; Central validates it.
   */
  returnTo?: string;

  /**
   * The access package, when the admin customized the Subrole's default.
   *
   * Central's invite route branches `if (templateId) … else if (scopes)`, and
   * `customScopes` UNIONS with the template expansion — so it can only widen.
   * A NARROWED invite therefore cannot be sent as template + custom at all.
   *
   *   widened  → `templateId` + `customScopes` (provenance kept)
   *   narrowed → `scopes` (Central records no templateId)
   *
   * Send the one that matches; the BFF picks the branch and Central still
   * expands and validates. Never send `scopes` and `templateId` together —
   * Central would silently discard `scopes`.
   */
  customScopes?: string[];
  scopes?: string[];

  /**
   * APP-AUTH-2 — the apps the administrator actually selected.
   *
   * Central intersects template expansion with this set, so a cross-app
   * Subrole cannot grant an app that was switched off. Sent as a real field
   * rather than inferred from `appRoles`, because the two are independent
   * inputs and Central treats the SELECTION as authoritative when they
   * disagree. Omitted entirely means "no selection expressed", which
   * preserves the pre-APP-AUTH-2 expansion exactly.
   */
  selectedAppIds?: string[];

  /**
   * Team placement, STAGED against the invite by the BFF and applied by
   * Central at acceptance — a pending invitee cannot hold a TeamMembership.
   * `teamRole` is normally omitted; 'Head' is the only value with an
   * authorization consumer.
   */
  teamId?: string;
  teamRole?: string;
  subroleId?: string;
  /**
   * Team name shown in the invitation email, e.g. "Technology › Sales".
   * Display only — the real placement is the staged assignment keyed by
   * `teamId`, which cannot exist until the invite has an id.
   */
  teamLabel?: string;
  /**
   * Subrole name shown in the invitation email, e.g. "Sales Representative".
   *
   * Display only. It exists because a NARROWED invite is sent as an explicit
   * scope array and therefore carries no `templateId`, leaving Central with
   * nothing to resolve the Subrole name from — the email then named the
   * workspace role instead.
   */
  subroleLabel?: string;
  /**
   * Providers to grant IN THE CHOSEN TEAM once the invitee accepts — Central
   * `providerConnectionId` + grant channel, nothing else. Requires `teamId`
   * (grants are Team-scoped). Checked by the BFF before the invite is created
   * and staged with the Team placement; Central makes the grants at
   * acceptance, authored by the inviter. Nothing is granted at send time.
   */
  providerSelections?: Array<{ providerConnectionId: string; channelType: string }>;
}

/** What the BFF reports back, including whether staging actually happened. */
export interface PlatformInviteResponse {
  ok: boolean;
  email?: string;
  role?: string;
  inviteId?: string | null;
  /**
   * The invite is created before the Team is staged, so staging can fail
   * without the invite failing. Reported rather than hidden.
   */
  teamAssignment?: { staged: boolean; reason?: string };
  /**
   * Present only when providers were chosen. `staged` < `requested` means
   * Central refused them at staging and the placement was saved without them.
   */
  providerAccess?: { requested: number; staged: number; reason?: string };
}

export const platformMembersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Reads ----
    // Each row may carry `appRoles`: Central's COMPLETE stored per-app grants.
    // The BFF passes it through verbatim and never defaults it, so `undefined`
    // (grants withheld from this caller) stays distinct from `{}` (known-empty)
    // all the way into the shared editor. Do not normalize it here.
    getPlatformMembers: builder.query<WorkspaceMemberWithAppRoles[], void>({
      query: () => ({ url: '/platform/members', method: 'GET' }),
      transformResponse: (body: MembersEnvelope) => body?.members ?? [],
      providesTags: [{ type: 'Team' as const, id: 'MEMBERS' }],
    }),

    getPlatformPendingInvites: builder.query<PendingInvite[], void>({
      query: () => ({ url: '/platform/invites', method: 'GET' }),
      transformResponse: (body: InvitesEnvelope) => body?.invites ?? [],
      providesTags: [{ type: 'Team' as const, id: 'INVITES' }],
    }),

    getPlatformInviteOptions: builder.query<InviteRoleOption[], void>({
      query: () => ({ url: '/platform/invite-options', method: 'GET' }),
      transformResponse: (body: OptionsEnvelope) => body?.options ?? [],
      providesTags: [{ type: 'Team' as const, id: 'INVITE_OPTIONS' }],
    }),

    // Central PR #479 — per-app invite assignments. Returned by the BFF verbatim
    // (Central's `AssignableAppsResponse` DTO); no transform, no recompute.
    getPlatformAssignableApps: builder.query<AssignableAppsResponse, void>({
      query: () => ({ url: '/platform/assignable-apps', method: 'GET' }),
      providesTags: [{ type: 'Team' as const, id: 'ASSIGNABLE_APPS' }],
    }),

    // ---- Invitation management (owner/admin — enforced by the BFF + Central) ----
    platformInviteMember: builder.mutation<PlatformInviteResponse, PlatformInviteRequest>({
      query: (body) => ({ url: '/platform/invites', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Team' as const, id: 'INVITES' },
        { type: 'Team' as const, id: 'MEMBERS' },
      ],
    }),

    platformResendInvite: builder.mutation<{ ok: boolean }, { inviteId: string }>({
      query: ({ inviteId }) => ({
        url: `/platform/invites/${encodeURIComponent(inviteId)}/resend`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Team' as const, id: 'INVITES' }],
    }),

    platformCancelInvite: builder.mutation<{ ok: boolean }, { inviteId: string }>({
      query: ({ inviteId }) => ({
        url: `/platform/invites/${encodeURIComponent(inviteId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Team' as const, id: 'INVITES' }],
    }),

    // ---- Member management (owner/admin) — preserves existing Members-tab UX ----
    platformUpdateMemberRole: builder.mutation<
      { ok: boolean },
      { userId: string; role: CentralRole }
    >({
      query: ({ userId, role }) => ({
        url: `/platform/members/${encodeURIComponent(userId)}`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: [{ type: 'Team' as const, id: 'MEMBERS' }],
    }),

    // Central PR #502 — replace a member's per-app grants.
    //
    // `appRoles` is the COMPLETE intended resulting map composed by the shared
    // editor (dormant grants included, already resubmitted). It is sent as-is:
    // no merge with cached members state here or in the BFF, because Central
    // uses REPLACE semantics — anything omitted is revoked. Invalidating
    // MEMBERS refetches the authoritative rows so the UI never settles on
    // optimistic local state.
    platformUpdateMemberAppRoles: builder.mutation<
      { ok: boolean },
      { userId: string; appRoles: InviteAppAssignments }
    >({
      query: ({ userId, appRoles }) => ({
        url: `/platform/members/${encodeURIComponent(userId)}/app-roles`,
        method: 'PATCH',
        body: { appRoles },
      }),
      invalidatesTags: [{ type: 'Team' as const, id: 'MEMBERS' }],
    }),

    platformRemoveMember: builder.mutation<{ ok: boolean }, { userId: string }>({
      query: ({ userId }) => ({
        url: `/platform/members/${encodeURIComponent(userId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Team' as const, id: 'MEMBERS' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPlatformMembersQuery,
  useGetPlatformPendingInvitesQuery,
  useGetPlatformInviteOptionsQuery,
  useGetPlatformAssignableAppsQuery,
  usePlatformInviteMemberMutation,
  usePlatformResendInviteMutation,
  usePlatformCancelInviteMutation,
  usePlatformUpdateMemberRoleMutation,
  usePlatformUpdateMemberAppRolesMutation,
  usePlatformRemoveMemberMutation,
} = platformMembersApi;
